import React, { useState, useEffect } from 'react';
import { Table, Input, Button, Tag, Space, Typography, Form, Select, Card, Image, Badge, TreeSelect } from 'antd';
import { SearchOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import cardsData from '../../mock/cards.json';
import datasetsData from '../../mock/datasets.json';

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;

// 组件类型映射
const componentTypeMap: Record<string, { category: string; name: string }> = {
  // 布局组件
  list: { category: '布局组件', name: '列表' },
  flex: { category: '布局组件', name: '弹性布局' },
  grid: { category: '布局组件', name: '网格' },
  collapse: { category: '布局组件', name: '折叠面板' },
  
  // 展示组件
  text: { category: '展示组件', name: '文本' },
  image: { category: '展示组件', name: '图片' },
  divider: { category: '展示组件', name: '分割线' },
  icon: { category: '展示组件', name: '图标' },
  chart: { category: '展示组件', name: '图表' },
  audio: { category: '展示组件', name: '音频' },
  video: { category: '展示组件', name: '视频' },
  progress: { category: '展示组件', name: '进度条' },
  
  // 交互组件
  button: { category: '交互组件', name: '按钮' },
  input: { category: '交互组件', name: '文本输入框' },
  number: { category: '交互组件', name: '数字输入框' },
  select: { category: '交互组件', name: '下拉单选' },
  cascader: { category: '交互组件', name: '级联选择' },
  radio: { category: '交互组件', name: '单选列表' },
  checkbox: { category: '交互组件', name: '多选列表' },
  switch: { category: '交互组件', name: '开关' },
  time: { category: '交互组件', name: '时间选择' },
  upload: { category: '交互组件', name: '文件上传' },
};

interface CardData {
  id: string;
  name: string;
  screenshot: string;
  dsl: any;
  status: string;
  tags: string[];
  datasets: string[];
  createTime: string;
  updateTime: string;
}

interface DatasetData {
  id: string;
  name: string;
  type: string;
  cardCount: number;
  createTime: string;
  updateTime: string;
}

interface DatasetDetailProps {
  collapsed?: boolean;
}

const DatasetDetail: React.FC<DatasetDetailProps> = ({ collapsed = false }) => {
  const [datasetCards, setDatasetCards] = useState<CardData[]>([]);
  const [searchText, setSearchText] = useState('');
  const [tagSearch, setTagSearch] = useState<string[]>([]);
  const [componentSearch, setComponentSearch] = useState<string[]>([]); // 新增组件筛选状态
  const [allTags, setAllTags] = useState<string[]>([]);
  const [tagTreeData, setTagTreeData] = useState<any[]>([]);
  const [componentTreeData, setComponentTreeData] = useState<any[]>([]); // 新增组件树形数据
  const [currentDataset, setCurrentDataset] = useState<DatasetData | null>(null);
  const [componentStats, setComponentStats] = useState<any>({});
  const [showStats, setShowStats] = useState(true); // 控制统计面板的显示与隐藏
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const navigate = useNavigate();
  const { datasetId } = useParams<{ datasetId: string }>();

  // 遍历DSL查找组件类型
  const findComponentsInDSL = (dsl: any, components: Set<string> = new Set()): Set<string> => {
    if (dsl && typeof dsl === 'object') {
      if (dsl.type) {
        components.add(dsl.type);
      }
      
      // 递归遍历子组件
      if (dsl.children && Array.isArray(dsl.children)) {
        dsl.children.forEach((child: any) => {
          findComponentsInDSL(child, components);
        });
      }
      
      // 遍历其他可能的对象属性
      Object.values(dsl).forEach((value: any) => {
        if (typeof value === 'object' && value !== null) {
          findComponentsInDSL(value, components);
        }
      });
    }
    
    return components;
  };

  // 所有需要展示的组件类型（确保全部显示）
  const allComponentTypes = {
    '布局组件': ['列表', '弹性布局', '网格', '折叠面板'],
    '展示组件': ['文本', '图片', '分割线', '图标', '图表', '音频', '视频', '进度条'],
    '交互组件': ['按钮', '文本输入框', '数字输入框', '下拉单选', '级联选择', '单选列表', '多选列表', '开关', '时间选择', '文件上传']
  };

  // 统计组件数据
  const calculateComponentStats = (cards: CardData[]) => {
    const stats: any = {
      '布局组件': {},
      '展示组件': {},
      '交互组件': {}
    };

    cards.forEach(card => {
      if (card.dsl && card.dsl.children) {
        traverseComponents(card.dsl.children, stats);
      }
    });

    setComponentStats(stats);
  };

  // 遍历组件树
  const traverseComponents = (components: any[], stats: any) => {
    components.forEach(component => {
      if (component.type) {
        const componentInfo = componentTypeMap[component.type];
        if (componentInfo) {
          const { category, name } = componentInfo;
          if (!stats[category][name]) {
            stats[category][name] = 0;
          }
          stats[category][name]++;
        }
      }
      
      // 递归处理子组件
      if (component.children && Array.isArray(component.children)) {
        traverseComponents(component.children, stats);
      }
    });
  };

  useEffect(() => {
    // 加载当前数据集信息
    const dataset = datasetsData.datasets.find(ds => ds.id === datasetId);
    if (dataset) {
      setCurrentDataset(dataset);
    } else {
      navigate('/dataset-management');
      return;
    }

    // 加载属于该数据集的卡片（使用数据集名称匹配）并只显示已标注的
    const cardsInDataset = cardsData.cards.filter(card => 
      card.datasets && card.datasets.includes(dataset!.name) && card.status === '已标注'
    );
    setDatasetCards(cardsInDataset);
    
    // 计算组件统计数据
    calculateComponentStats(cardsInDataset);
    
    // 提取所有唯一的标签
    const tags = new Set<string>();
    
    cardsInDataset.forEach(card => {
      card.tags.forEach(tag => tags.add(tag));
    });
    
    setAllTags(Array.from(tags));
    
    // 生成标签树形结构数据
    const tagTreeData = [
      {
        title: '设计风格',
        value: 'design-style',
        key: 'design-style',
        children: [
          { title: '现代简约', value: '现代简约', key: '现代简约' },
          { title: '新闻资讯', value: '新闻资讯', key: '新闻资讯' },
          { title: '多媒体', value: '多媒体', key: '多媒体' },
          { title: '表单交互', value: '表单交互', key: '表单交互' },
          { title: '数据图表', value: '数据图表', key: '数据图表' },
        ],
      },
      {
        title: '业务领域',
        value: 'business-domain',
        key: 'business-domain',
        children: [
          { title: '电商', value: '电商', key: '电商' },
          { title: '媒体', value: '媒体', key: '媒体' },
          { title: '娱乐', value: '娱乐', key: '娱乐' },
          { title: '工具', value: '工具', key: '工具' },
          { title: '数据分析', value: '数据分析', key: '数据分析' },
        ],
      },
      {
        title: '功能类型',
        value: 'function-type',
        key: 'function-type',
        children: [
          { title: '商品展示', value: '商品展示', key: '商品展示' },
          { title: '信息展示', value: '信息展示', key: '信息展示' },
          { title: '视频播放', value: '视频播放', key: '视频播放' },
          { title: '用户输入', value: '极用户输入', key: '用户输入' },
          { title: '可视化', value: '可视化', key: '可视化' },
        ],
      },
    ];
    
    setTagTreeData(tagTreeData);
    
    // 生成组件树形结构数据
    const componentTreeData = [
      {
        title: '布局组件',
        value: 'layout',
        key: 'layout',
        children: [
          { title: '列表', value: 'list', key: 'list' },
          { title: '弹性布局', value: 'flex', key: 'flex' },
          { title: '网格', value: 'grid', key: 'grid' },
          { title: '折叠面板', value: 'collapse', key: 'collapse' },
        ],
      },
      {
        title: '展示组件',
        value: 'display',
        key: 'display',
        children: [
          { title: '文本', value: 'text', key: 'text' },
          { title: '图片', value: 'image', key: 'image' },
          { title: '分割线', value: 'divider', key: 'divider' },
          { title: '图标', value: 'icon', key: 'icon' },
          { title: '图表', value: 'chart', key: 'chart' },
          { title: '音频', value: 'audio', key: 'audio' },
          { title: '视频', value: 'video', key: 'video' },
          { title: '进度条', value: 'progress', key: 'progress' },
        ],
      },
      {
        title: '交互组件',
        value: 'interactive',
        key: 'interactive',
        children: [
          { title: '按钮', value: 'button', key: 'button' },
          { title: '文本输入框', value: 'input', key: 'input' },
          { title: '数字输入框', value: 'number', key: 'number' },
          { title: '下拉单选', value: 'select', key: 'select' },
          { title: '级联选择', value: 'cascader', key: 'cascader' },
          { title: '单选列表', value: 'radio', key: 'radio' },
          { title: '多选列表', value: 'checkbox', key: 'checkbox' },
          { title: '开关', value: 'switch', key: 'switch' },
          { title: '时间选择', value: 'time', key: 'time' },
          { title: '文件上传', value: 'upload', key: 'upload' },
        ],
      },
    ];
    
    setComponentTreeData(componentTreeData);
  }, [datasetId, navigate]);

  // 处理搜索
  const handleSearch = () => {
    const cardsInDataset = cardsData.cards.filter(card => 
      card.datasets && card.datasets.includes(currentDataset!.name) && card.status === '已标注'
    );
    let filteredCards = [...cardsInDataset];
    
    // 按名称筛选
    if (searchText) {
      filteredCards = filteredCards.filter(card => 
        card.name.toLowerCase().includes(searchText.toLowerCase())
      );
    }
    
    // 按卡片标签筛选（或条件）
    if (tagSearch && tagSearch.length > 0) {
      filteredCards = filteredCards.filter(card => 
        tagSearch.some(tag => card.tags.includes(tag))
      );
    }
    
    // 按组件类型筛选
    if (componentSearch && componentSearch.length > 0) {
      filteredCards = filteredCards.filter(card => {
        // 查找卡片DSL中包含的组件类型
        const cardComponents = findComponentsInDSL(card.dsl);
        // 检查是否有匹配的组件类型
        return componentSearch.some(comp => cardComponents.has(comp));
      });
    }
    
    setDatasetCards(filteredCards);
    // 重新计算组件统计数据
    calculateComponentStats(filteredCards);
  };

  // 处理返回
  const handleBack = () => {
    navigate('/dataset-management');
  };

  // 计算DSL的token数量（简化实现）
  const calculateTokenCount = (dsl: any): number => {
    // 这里是一个简化的token计算方法，实际项目中可能需要更复杂的逻辑
    return JSON.stringify(dsl).length;
  };

  // 计算平均token长度
  const calculateAverageTokenLength = (cards: CardData[]): number => {
    if (cards.length === 0) return 0;
    
    const totalTokens = cards.reduce((sum, card) => {
      return sum + calculateTokenCount(card.dsl);
    }, 0);
    
    return Math.round(totalTokens / cards.length);
  };

  // 渲染组件统计信息
  const renderComponentStats = () => {
    const averageTokenLength = calculateAverageTokenLength(datasetCards);
    
    return (
      <Card 
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>组件数据统计 (<Text strong>平均Token长度:{averageTokenLength}</Text>)</span>
          </div>
        }
        style={{ marginBottom: 16 }}
        extra={
          <Button 
            type="link" 
            onClick={() => setShowStats(!showStats)}
          >
            {showStats ? '收起' : '展开'}
          </Button>
        }
      >
        {showStats && (
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            {Object.keys(allComponentTypes).map(category => (
              <div key={category} style={{ flex: 1, padding: '0 16px' }}>
                <Title level={5}>{category}</Title>
                <ul>
                  {allComponentTypes[category as keyof typeof allComponentTypes].map(name => (
                    <li key={name} style={{ marginBottom: 8 }}>
                      <span>{name}: </span>
                      <span style={{ fontWeight: 'bold' }}>
                        {(componentStats[category]?.[name] || 0)}个
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </Card>
    );
  };

  // 处理分页变化
  const handleTableChange = (pagination: any) => {
    setPagination({
      current: pagination.current,
      pageSize: pagination.pageSize,
    });
  };

  // 表格列定义
  const cardColumns = [
    {
      title: '序号',
      key: 'index',
      width: 60,
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: '卡片名称',
      dataIndex: 'name',
      key: 'name',
      width: 150,
    },
    {
      title: '卡片截图',
      dataIndex: 'screenshot',
      key: 'screenshot',
      width: 120,
      render: (text: string) => (
        <Image
          width={80}
          src={text}
          fallback="data:image/png;base64,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"
        />
      ),
    },
    {
      title: '卡片状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => (
        <Badge 
          status={status === '已标注' ? 'success' : 'default'} 
          text={status} 
        />
      ),
    },
    {
      title: '卡片标签',
      dataIndex: 'tags',
      key: 'tags',
      width: 200,
      render: (tags: string[]) => (
        <span>
          {tags.map(tag => (
            <Tag key={tag} color="green" style={{ marginBottom: 4 }}>
              {tag}
            </Tag>
          ))}
        </span>
      ),
    },
    {
      title: '包含组件',
      key: 'components',
      width: 200,
      render: (_: any, record: CardData) => {
        // 查找卡片DSL中包含的组件类型
        const cardComponents = Array.from(findComponentsInDSL(record.dsl));
        // 映射组件类型到显示名称
        const componentNames = cardComponents
          .map(type => componentTypeMap[type]?.name || type)
          .filter(name => name); // 过滤掉空值
        
        return (
          <span>
            {componentNames.map((name, index) => (
              <Tag color="blue" key={index} style={{ marginBottom: 4 }}>
                {name}
              </Tag>
            ))}
          </span>
        );
      },
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      key: 'createTime',
      width: 150,
      render: (date: string) => new Date(date).toLocaleString()
    }
  ];

  return (
    <div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* 组件数据统计 */}
        {renderComponentStats()}
        
        {/* 卡片列表区域 */}
        <Card title={`${currentDataset?.name || ''}`}>
          <div style={{ marginBottom: 16 }}>
            <Form 
              layout="inline" 
              style={{ marginBottom: 16 }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
            >
              <Form.Item label="卡片名称">
                <Input
                  placeholder="搜索卡片名称"
                  allowClear
                  value={searchText}
                  onChange={e => setSearchText(e.target.value)}
                  style={{ width: 200 }}
                />
              </Form.Item>
              
              <Form.Item label="卡片标签">
                <TreeSelect
                  treeData={tagTreeData}
                  placeholder="选择标签"
                  value={tagSearch}
                  onChange={setTagSearch}
                  treeCheckable={true}
                  showCheckedStrategy={TreeSelect.SHOW_CHILD}
                  allowClear
                  style={{ width: 200 }}
                />
              </Form.Item>
              
              <Form.Item label="包含组件">
                <TreeSelect
                  treeData={componentTreeData}
                  placeholder="选择组件"
                  value={componentSearch}
                  onChange={setComponentSearch}
                  treeCheckable={true}
                  showCheckedStrategy={TreeSelect.SHOW_CHILD}
                  allowClear
                  style={{ width: 200 }}
                />
              </Form.Item>
              
              <Form.Item>
                <Button type="primary" onClick={handleSearch}>
                  搜索
                </Button>
              </Form.Item>
              
              <Form.Item>
                <Button onClick={() => {
                  setSearchText('');
                  setTagSearch([]);
                  setComponentSearch([]);
                  handleSearch();
                }}>
                  重置
                </Button>
              </Form.Item>
            </Form>
          </div>

          <Table
            columns={cardColumns}
            dataSource={datasetCards}
            rowKey="id"
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条/共 ${total} 条`,
              pageSizeOptions: ['10', '20', '50', '100', '200', '500']
            }}
            onChange={handleTableChange}
            size="small"
          />
        </Card>
      </div>
    </div>
  );
};

export default DatasetDetail;